import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const TutorialOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10015;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
  
  /* Ensure proper centering for mobile */
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Override centering for non-fullscreen steps */
  ${props => !props.isWelcomeSlide && `
    display: block;
  `}
`;

const Spotlight = styled.div`
  position: absolute;
  border: 3px solid #FF4500;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15); /* Semi-transparent white to brighten/clear the area */
  box-shadow: 0 0 0 4px rgba(255, 69, 0, 0.3), 
              0 0 20px rgba(255, 69, 0, 0.5);
  pointer-events: none;
  z-index: 10015;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  opacity: ${props => props.isPositioning ? 0 : 1};
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% { box-shadow: 0 0 0 4px rgba(255, 69, 0, 0.3), 0 0 20px rgba(255, 69, 0, 0.5); }
    50% { box-shadow: 0 0 0 8px rgba(255, 69, 0, 0.4), 0 0 30px rgba(255, 69, 0, 0.7); }
    100% { box-shadow: 0 0 0 4px rgba(255, 69, 0, 0.3), 0 0 20px rgba(255, 69, 0, 0.5); }
  }
`;

const ClickableArea = styled.div`
  position: absolute;
  cursor: pointer;
  z-index: 10016;
  border-radius: 12px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  opacity: ${props => props.isPositioning ? 0 : 1};
`;

const WelcomeBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: 
    radial-gradient(circle at 20% 30%, rgba(255, 35, 0, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(255, 69, 0, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(255, 101, 0, 0.08) 0%, transparent 50%);
  pointer-events: none;
  z-index: 10014;
  opacity: 0;
  animation: backgroundFadeIn 1.5s ease-out 0.5s forwards;
  
  /* Mobile adjustments - reduce intensity */
  @media (max-width: 480px) {
    background: 
      radial-gradient(circle at 20% 30%, rgba(255, 35, 0, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(255, 69, 0, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(255, 101, 0, 0.04) 0%, transparent 50%);
  }
  
  @keyframes backgroundFadeIn {
    to { opacity: 1; }
  }
`;

const Tooltip = styled.div`
  position: absolute;
  background: linear-gradient(135deg, #FF2300 0%, #FF4500 100%);
  color: white;
  padding: 20px;
  border-radius: 16px;
  max-width: 280px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  z-index: 10017;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  opacity: ${props => props.isPositioning ? 0 : 1};
  
  /* Ensure proper positioning for full-screen welcome slide */
  ${props => props.isWelcomeSlide && `
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
  `}
  
  /* Enhanced animations for welcome slide */
  ${props => props.isWelcomeSlide && `
    max-width: 420px;
    width: calc(100vw - 32px);
    padding: 32px;
    border-radius: 24px;
    background: linear-gradient(135deg, #FF2300 0%, #FF4500 50%, #FF6500 100%);
    box-shadow: 
      0 20px 60px rgba(255, 35, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    
    /* Mobile responsive adjustments */
    @media (max-width: 480px) {
      max-width: calc(100vw - 24px);
      width: calc(100vw - 24px);
      padding: 24px 20px;
      border-radius: 20px;
      margin: 0 12px;
    }
    
    @media (max-width: 375px) {
      padding: 20px 16px;
      border-radius: 16px;
      margin: 0 8px;
    }
    
    /* Slide in from top animation - preserve centering */
    animation: slideInFromTop 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    opacity: 0;
    
    @keyframes slideInFromTop {
      0% {
        transform: translate(-50%, -50%) translateY(-50px) scale(0.9);
        opacity: 0;
      }
      60% {
        transform: translate(-50%, -50%) translateY(5px) scale(1.02);
        opacity: 0.9;
      }
      100% {
        transform: translate(-50%, -50%) translateY(0) scale(1);
        opacity: 1;
      }
    }
    
    /* Subtle breathing animation after entrance */
    &.breathing {
      animation: slideInFromTop 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards,
                 breathe 4s ease-in-out 1s infinite;
    }
    
    @keyframes breathe {
      0%, 100% {
        transform: translate(-50%, -50%) scale(1);
        box-shadow: 
          0 20px 60px rgba(255, 35, 0, 0.4),
          0 0 0 1px rgba(255, 255, 255, 0.1),
          inset 0 1px 0 rgba(255, 255, 255, 0.2);
      }
      50% {
        transform: translate(-50%, -50%) scale(1.005);
        box-shadow: 
          0 25px 70px rgba(255, 35, 0, 0.5),
          0 0 0 1px rgba(255, 255, 255, 0.15),
          inset 0 1px 0 rgba(255, 255, 255, 0.25);
      }
    }
  `}
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
  }
  
  /* Arrow pointing down */
  &.arrow-down::before {
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #FF2300;
  }
  
  /* Arrow pointing up */
  &.arrow-up::before {
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #FF4500;
  }
  
  /* Arrow pointing left */
  &.arrow-left::before {
    top: 50%;
    right: -10px;
    transform: translateY(-50%);
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 10px solid #FF4500;
  }
  
  /* Arrow pointing right */
  &.arrow-right::before {
    top: 50%;
    left: -10px;
    transform: translateY(-50%);
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid #FF2300;
  }
`;

const TooltipTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: white;
  
  /* Enhanced styling for welcome slide */
  ${props => props.isWelcomeSlide && `
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 12px 0;
    text-align: center;
    background: linear-gradient(45deg, #ffffff 0%, #ffeeaa 50%, #ffffff 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-size: 200% 200%;
    animation: titleShimmer 3s ease-in-out infinite,
               titleSlideIn 0.6s ease-out 0.3s both;
    
    /* Mobile responsive font sizes */
    @media (max-width: 480px) {
      font-size: 16px;
      margin: 0 0 10px 0;
    }
    
    @media (max-width: 375px) {
      font-size: 15px;
      margin: 0 0 8px 0;
    }
    
    @keyframes titleShimmer {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    
    @keyframes titleSlideIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `}
`;

const TooltipText = styled.p`
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 16px 0;
  color: rgba(255, 255, 255, 0.9);
  
  /* Enhanced styling for welcome slide */
  ${props => props.isWelcomeSlide && `
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 32px 0;
    text-align: left;
    color: rgba(255, 255, 255, 0.95);
    opacity: 0;
    animation: textFadeIn 0.8s ease-out 0.8s both;
    
    /* Mobile responsive adjustments */
    @media (max-width: 480px) {
      font-size: 13px;
      line-height: 1.4;
      margin: 0 0 24px 0;
    }
    
    @media (max-width: 375px) {
      font-size: 12px;
      margin: 0 0 20px 0;
    }
    
    @keyframes textFadeIn {
      from {
        opacity: 0;
        transform: translateY(15px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `}
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const StepCounter = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const TutorialButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: transparent;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Simple styling for welcome slide buttons - like onboarding */
  ${props => props.isWelcomeSlide && `
    background: transparent;
    border: none;
    padding: 0;
    font-size: 12px;
    font-weight: normal;
    color: white;
    opacity: 0;
    animation: buttonSlideIn 0.6s ease-out 1.2s both;
      
      &:hover {
      background: transparent;
      border: none;
      opacity: 0.8;
      }
    
    @keyframes buttonSlideIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `}
`;

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: '',
    description: 'Let\'s take a quick tour to show you the key features that will make your life easier.',
    target: null,
    fullScreen: true
  },
  {
    id: 'sidebar-menu',
    title: 'Navigation Menu',
    description: 'This is your main navigation. Tap here to access different sections of your business dashboard.',
    target: 'hamburger-menu'
  },
  {
    id: 'home-tabs',
    title: 'Home Navigation',
    description: 'Switch between Chat, Activity, and Apps using these tabs. Tap Apps to view your available tools.',
    target: 'home-tabs'
  },
  {
    id: 'bookings-app',
    title: 'Bookings Management',
    description: 'Manage your appointments, gigs, and client bookings. This is your central hub for scheduling.',
    target: 'bookings-app',
    requiresAppsTab: true
  },
  {
    id: 'catalog-app', 
    title: 'Music Catalog',
    description: 'Upload, organize, and showcase your music portfolio. Perfect for sharing your work with clients.',
    target: 'catalog-app',
    requiresAppsTab: true
  },
  {
    id: 'flows-app',
    title: 'Workflows',
    description: 'Manage workflows and quick reminders for releases and shows.',
    target: 'flows-app',
    requiresAppsTab: true
  },
  {
    id: 'knowledge-base-app',
    title: 'Knowledge Base',
    description: 'Store and organize important information, documents, and knowledge for easy access.',
    target: 'knowledge-base-app',
    requiresAppsTab: true
  },
  {
    id: 'ai-chat',
    title: 'AI Assistant',
    description: 'Your intelligent business assistant. Ask questions, get insights, or request help with any task.',
    target: 'ai-input'
  }
];

const DizeeTutorial = ({ onClose, startAtStep = 0 }) => {
  const [currentStep, setCurrentStep] = useState(startAtStep);
  const [isVisible, setIsVisible] = useState(true);
  const [spotlightPosition, setSpotlightPosition] = useState({});
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [clickablePosition, setClickablePosition] = useState({});
  const [showBreathingAnimation, setShowBreathingAnimation] = useState(false);
  // Start with positioning true if not on welcome slide
  const [isPositioning, setIsPositioning] = useState(startAtStep > 0);

  const getCurrentStep = () => TUTORIAL_STEPS[currentStep];
  const isLastStep = currentStep >= TUTORIAL_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const findElement = (targetId) => {
    const selectors = {
      'hamburger-menu': [
        'div[class*="w-[70px]"][class*="h-[40px]"]',
        'div[class*="cursor-pointer"]:has(svg)',
        'svg[viewBox="0 0 22 8"]',
        'div:has(svg[viewBox="0 0 22 8"])',
        '.line2-container'
      ],
      'home-tabs': [
        'div:has(> button):has-text("Chat"):has-text("Activity"):has-text("Apps")',
      ],
      'apps-tab': [
        'button:has-text("Apps")'
      ],
      'chat-tab': [
        'button:has-text("Chat")'
      ],
      'bookings-app': [
        '.bookings-app-icon',
        'div[class*="bookings-app"]',
        'div[color="#FFFF00"]'
      ],
      'catalog-app': [
        '.catalog-app-icon',
        'div[class*="catalog-app"]', 
        'div[color="#17A2B8"]'
      ],
      'flows-app': [
        '.flows-app-icon',
        'div[class*="flows-app"]',
        'div[color="#FF3B30"]'
      ],
      'knowledge-base-app': [
        '.knowledge-base-app-icon',
        'div[class*="knowledge-base-app"]',
        'div[color="#5856D6"]'
      ],
      'ai-input': [
        // Target the actual input field with "Ask dizee anything..." placeholder
        'input[placeholder*="Ask dizee anything"]',
        'input[placeholder*="Ask dizee"]',
        'input[placeholder*="ask dizee"]'
      ],
      'preset-prompts': [
        '.homepage-preset-item',
        '.homepage-presets-row > div',
        '.preset-item'
      ],
      'bottom-nav': [
        '.homepage-navigation-section',
        '.homepage-nav-item',
        '.bottom-navigation'
      ]
    };

    const elementSelectors = selectors[targetId] || [];
    
    // Special handling for tab buttons - find by text content
    if (targetId === 'apps-tab' || targetId === 'chat-tab' || targetId === 'home-tabs') {
      const buttons = document.querySelectorAll('button');
      for (const button of buttons) {
        if (targetId === 'apps-tab' && button.textContent.trim() === 'Apps') {
          return button;
        }
        if (targetId === 'chat-tab' && button.textContent.trim() === 'Chat') {
          return button;
        }
      }
      
      // For home-tabs, find the container with all three buttons as DIRECT children
      if (targetId === 'home-tabs') {
        const containers = document.querySelectorAll('div');
        for (const container of containers) {
          // Get DIRECT button children only (not nested)
          const directButtons = Array.from(container.children).filter(child => child.tagName === 'BUTTON');
          if (directButtons.length === 3) {
            const texts = directButtons.map(b => b.textContent.trim());
            if (texts.includes('Chat') && texts.includes('Activity') && texts.includes('Apps')) {
              console.log('Found home-tabs container:', container);
              return container;
            }
          }
        }
      }
    }
    
    // Try each selector
    for (const selector of elementSelectors) {
      try {
        const element = document.querySelector(selector);
        if (element && element.offsetParent !== null) {
          return element;
        }
      } catch (e) {
        // Invalid selector, continue to next
        continue;
      }
    }

    // For AI input, find ANY visible input in footer area - keep it simple
    if (targetId === 'ai-input') {
      console.log('Looking for AI input...');
      
      // Just find ANY input that's in the bottom half of the screen
      const allInputs = document.querySelectorAll('input');
      console.log('Found inputs:', allInputs.length);
      
      for (const input of allInputs) {
        if (input.offsetParent !== null) {
          const rect = input.getBoundingClientRect();
          console.log('Input rect:', rect, 'placeholder:', input.placeholder);
          
          // If it's in the bottom half and has reasonable size, use it
          if (rect.top > window.innerHeight * 0.5 && rect.width > 50) {
            console.log('Using this input for AI step');
            return input;
          }
        }
      }
      
      console.log('No suitable input found, will use fallback');
      return null; // Use fallback positioning
    }

    // For app icons, try finding by color
    if (targetId.includes('-app')) {
      const colorMap = {
        'bookings-app': ['#FFFF00', 'rgb(255, 255, 0)', 'yellow'],
        'catalog-app': ['#17A2B8', 'rgb(23, 162, 184)', 'teal'],
        'merch-app': ['#9932CC', 'rgb(153, 50, 204)', 'purple'],
        'analytics-app': ['#34C759', 'rgb(52, 199, 89)', 'green'],
        'pages-app': ['#FF3B30', 'rgb(255, 59, 48)', 'red']
      };
      
      const targetColors = colorMap[targetId] || [];
      const allElements = document.querySelectorAll('div, button');
      
      for (const element of allElements) {
        const styles = window.getComputedStyle(element);
        const bgColor = styles.backgroundColor.toLowerCase();
        const color = styles.color.toLowerCase();
        
        for (const targetColor of targetColors) {
          if (bgColor.includes(targetColor.toLowerCase()) || 
              color.includes(targetColor.toLowerCase()) ||
              element.style.backgroundColor.includes(targetColor) ||
              element.getAttribute('color') === targetColor) {
            return element;
          }
        }
      }
    }

    

    // For preset prompts, find the first visible preset button
    if (targetId === 'preset-prompts') {
      const presetContainers = document.querySelectorAll('div');
      for (const container of presetContainers) {
        if (container.children.length >= 4 && 
            container.offsetParent !== null &&
            container.getBoundingClientRect().width > 100) {
          return container;
        }
      }
    }

    // For hamburger menu, find the Line2 SVG or its container
    if (targetId === 'hamburger-menu') {
      // Look for Line2 SVG specifically
      const line2Svg = document.querySelector('svg[viewBox="0 0 22 8"]');
      if (line2Svg) {
        // Return the parent div container which has the click handler
        return line2Svg.parentElement;
      }
      
      // Fallback: look for divs in header area with cursor-pointer
      const headerDivs = document.querySelectorAll('div[class*="cursor-pointer"]');
      for (const div of headerDivs) {
        const rect = div.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.2 && 
            rect.left < window.innerWidth * 0.3 &&
            div.querySelector('svg')) {
          return div;
        }
      }
    }

    // For bottom nav, find navigation at bottom of screen
    if (targetId === 'bottom-nav') {
      const navElements = document.querySelectorAll('div, nav, footer');
      for (const nav of navElements) {
        const rect = nav.getBoundingClientRect();
        if (rect.bottom > window.innerHeight - 150 && 
            rect.width > 200 &&
            nav.children.length >= 3) {
          return nav;
        }
      }
    }

    return null;
  };

  const updatePositions = () => {
    const step = getCurrentStep();
        const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    if (step.fullScreen) {
      setSpotlightPosition({});
      
      // For welcome slide, positioning is handled by CSS
      // Just set minimal positioning to avoid conflicts
      setTooltipPosition({
        className: ''
      });
      
      setClickablePosition({});
      return;
    }

    const element = findElement(step.target);
    
    if (element) {
      const rect = element.getBoundingClientRect();
      
      // Spotlight positioning with better bounds checking
      const currentTutorialStep = getCurrentStep();
      
      // Determine padding based on element type
      let horizontalPadding = 10; // Default horizontal padding
      let verticalPadding = 10; // Default vertical padding
      
      // For tab buttons and tab container, use minimal padding to fit precisely
      if (currentTutorialStep.target === 'apps-tab' || 
          currentTutorialStep.target === 'chat-tab') {
        horizontalPadding = 0; // Zero horizontal padding for individual tab buttons
        verticalPadding = 5; // Keep normal vertical padding
      } else if (currentTutorialStep.target === 'home-tabs') {
        horizontalPadding = 8; // Small horizontal padding for tab container
        verticalPadding = 8; // Small vertical padding for tab container
      }
      
      let spotlightLeft = Math.max(0, rect.left - horizontalPadding);
      let spotlightTop = Math.max(0, rect.top - verticalPadding);
      let spotlightWidth = rect.width + (horizontalPadding * 2);
      let spotlightHeight = rect.height + (verticalPadding * 2);
      
      // Simple positioning for AI input
      if (currentTutorialStep.target === 'ai-input') {
        console.log('Positioning AI input spotlight. Element rect:', rect);
        console.log('Viewport height:', viewportHeight);
        
        // If element is below screen, force it to bottom area of visible screen
        if (rect.top > viewportHeight) {
          console.log('Element is below screen, forcing to bottom of viewport');
          const baseWidth = 385; // 350 + 10% (35px)
          spotlightLeft = Math.max(10, (viewportWidth - baseWidth) / 2); // Wider spotlight
          spotlightTop = viewportHeight - 160; // More space from bottom (100 + 60) - KEEP TOP SAME
          spotlightWidth = Math.min(baseWidth, viewportWidth - 20); // 10% wider to cover full input area
          spotlightHeight = 148; // Extend bottom by 10px more (138 + 10px)
        } else {
          // Use element's actual position but make it bigger
          spotlightLeft = rect.left - 20; // More padding on left
          spotlightTop = rect.top - 45; // More padding on top (15 + 30) - KEEP TOP SAME
          spotlightWidth = rect.width + 44; // 10% wider (40 + 4px)
          spotlightHeight = rect.height + 113.5; // Extend bottom by 10px more (103.5 + 10px)
        }
        
        console.log('Final AI spotlight position:', { spotlightLeft, spotlightTop, spotlightWidth, spotlightHeight });
    } else {
        // Standard positioning for other elements
        spotlightWidth = Math.min(spotlightWidth, viewportWidth - spotlightLeft);
        spotlightHeight = Math.min(spotlightHeight, viewportHeight - spotlightTop);
      }
      
      const spotlightStyle = {
        left: spotlightLeft,
        top: spotlightTop,
        width: Math.max(0, spotlightWidth),
        height: Math.max(0, spotlightHeight)
      };
      
      // Clickable area positioning
      const clickableStyle = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      };
      
      // Tooltip positioning optimized for mobile
      let tooltipStyle = {};
      let arrowClass = '';
      
      const tooltipWidth = Math.min(280, viewportWidth - 40);
      const tooltipHeight = 160; // Estimated with buttons
      const padding = 20;
      const step = getCurrentStep();
      
      // Special handling for AI input - always keep tooltip visible on screen
      if (step.target === 'ai-input') {
        // Force tooltip to center of screen for AI input step
        tooltipStyle = {
          top: Math.max(padding, (viewportHeight - tooltipHeight) / 2), // Center vertically
          left: Math.max(padding, (viewportWidth - tooltipWidth) / 2) // Center horizontally
        };
        arrowClass = ''; // No arrow since we're centering
      }
      else if (rect.bottom > viewportHeight * 0.8) {
        tooltipStyle = {
          top: Math.max(padding, rect.top - tooltipHeight - 20), // Just 20px above input container
          left: Math.max(padding, Math.min(rect.left + rect.width/2 - tooltipWidth/2, viewportWidth - tooltipWidth - padding))
        };
        arrowClass = 'arrow-up';
      }
      // Try positioning below element first (best for mobile)
      else if (rect.bottom + tooltipHeight + padding < viewportHeight) {
        tooltipStyle = {
          top: rect.bottom + 15,
          left: Math.max(padding, Math.min(rect.left + rect.width/2 - tooltipWidth/2, viewportWidth - tooltipWidth - padding))
        };
        arrowClass = 'arrow-down';
      }
      // Try positioning above element
      else if (rect.top - tooltipHeight - padding > 0) {
        tooltipStyle = {
          top: rect.top - tooltipHeight - 15,
          left: Math.max(padding, Math.min(rect.left + rect.width/2 - tooltipWidth/2, viewportWidth - tooltipWidth - padding))
        };
        arrowClass = 'arrow-up';
      }
      // For bottom elements, force tooltip above with more space
      else if (rect.bottom > viewportHeight * 0.7) {
        tooltipStyle = {
          top: Math.max(padding, rect.top - tooltipHeight - 15),
          left: Math.max(padding, Math.min(rect.left + rect.width/2 - tooltipWidth/2, viewportWidth - tooltipWidth - padding))
        };
        arrowClass = 'arrow-up';
      }
      // For top elements, force tooltip below
      else if (rect.top < viewportHeight * 0.3) {
        tooltipStyle = {
          top: Math.min(rect.bottom + 15, viewportHeight - tooltipHeight - padding),
          left: Math.max(padding, Math.min(rect.left + rect.width/2 - tooltipWidth/2, viewportWidth - tooltipWidth - padding))
        };
        arrowClass = 'arrow-down';
      }
      // Fallback: center tooltip with no arrow (safest option)
      else {
        tooltipStyle = {
          top: Math.max(padding, Math.min(viewportHeight/2 - tooltipHeight/2, viewportHeight - tooltipHeight - padding)),
          left: Math.max(padding, viewportWidth/2 - tooltipWidth/2)
        };
        arrowClass = '';
      }
      
      setSpotlightPosition(spotlightStyle);
      setTooltipPosition({ ...tooltipStyle, className: arrowClass });
      setClickablePosition(clickableStyle);
      } else {
      // Fallback positioning when element not found
      const step = getCurrentStep();
      
      if (step.target === 'ai-input') {
        // For AI input fallback, position in footer area where input should be
        console.log('AI input element not found, using footer fallback positioning');
        const fallbackHeight = 40;
        const fallbackWidth = Math.min(300, viewportWidth - 40);
        const fallbackY = Math.max(viewportHeight - 100, viewportHeight * 0.85); // Near bottom
        const fallbackX = (viewportWidth - fallbackWidth) / 2;
        
        setSpotlightPosition({
          left: fallbackX,
          top: fallbackY,
          width: fallbackWidth,
          height: fallbackHeight
        });
        setTooltipPosition({
          top: Math.max(20, fallbackY - 180), // Position tooltip above
          left: Math.max(20, (viewportWidth - 280) / 2),
          className: 'arrow-up'
        });
        setClickablePosition({
          left: fallbackX,
          top: fallbackY,
          width: fallbackWidth,
          height: fallbackHeight
        });
      } else {
        // General fallback positioning - center screen
        const fallbackHeight = Math.min(120, viewportHeight * 0.15);
        const fallbackWidth = Math.min(250, viewportWidth - 40);
        const fallbackY = viewportHeight * 0.4;
        const fallbackX = (viewportWidth - fallbackWidth) / 2;
        
        setSpotlightPosition({
          left: fallbackX,
          top: fallbackY,
          width: fallbackWidth,
          height: fallbackHeight
        });
        setTooltipPosition({
          top: Math.min(fallbackY + fallbackHeight + 20, viewportHeight - 180),
          left: Math.max(20, (viewportWidth - 280) / 2),
          className: 'arrow-up'
        });
        setClickablePosition({
          left: fallbackX,
          top: fallbackY,
          width: fallbackWidth,
          height: fallbackHeight
        });
      }
    }
  };

  // Handle automatic tab switching when needed
  useEffect(() => {
    const step = getCurrentStep();
    
    // Clear positions immediately to prevent flash
    setSpotlightPosition({});
    setTooltipPosition({});
    setClickablePosition({});
    
    // Fade out spotlight for smooth transition
    setIsPositioning(true);
    
    // Wait for fade-out to complete before making any changes
    setTimeout(() => {
      // If step requires apps tab to be active, click it
      if (step.requiresAppsTab) {
        const buttons = document.querySelectorAll('button');
        for (const button of buttons) {
          if (button.textContent.trim() === 'Apps') {
            button.click();
            break;
          }
        }
      }
      
      // If step is AI input, ensure chat tab is active
      if (step.target === 'ai-input') {
        const buttons = document.querySelectorAll('button');
        for (const button of buttons) {
          if (button.textContent.trim() === 'Chat') {
            button.click();
            break;
          }
        }
      }
      
      // Additional delay to ensure DOM is fully ready and tabs have switched
      setTimeout(() => {
        updatePositions();
        // Fade in spotlight after positioning is complete
        setTimeout(() => {
          setIsPositioning(false);
        }, 100);
      }, 350);
    }, 200); // Slightly longer wait for fade-out to be visible
  }, [currentStep]);

  useEffect(() => {
    window.addEventListener('resize', updatePositions);
    return () => window.removeEventListener('resize', updatePositions);
  }, []);

  // Trigger breathing animation after welcome slide entrance
  useEffect(() => {
    if (isFirstStep) {
      setShowBreathingAnimation(false);
      const timer = setTimeout(() => {
        setShowBreathingAnimation(true);
      }, 1000); // Start breathing after entrance animation completes
      return () => clearTimeout(timer);
    }
  }, [isFirstStep]);

  const handleNext = () => {
    if (isLastStep) {
      handleClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
        setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleSpotlightClick = () => {
    const step = getCurrentStep();
    if (step.target) {
      const element = findElement(step.target);
      if (element && element.click) {
        // If this step should click to reveal content, actually click it
        if (step.clickToReveal) {
          element.click();
          // Wait for content to be revealed before moving to next step
          setTimeout(() => {
            handleNext();
          }, 300);
        } else {
          // Just move to next step
          setTimeout(() => {
            handleNext();
          }, 100);
        }
      } else {
        handleNext();
      }
    } else {
      handleNext();
    }
  };

  const step = getCurrentStep();
  const isWelcomeSlide = isFirstStep;
  
  return (
    <TutorialOverlay isVisible={isVisible} isWelcomeSlide={isWelcomeSlide}>
      {/* Enhanced background for welcome slide */}
      {isWelcomeSlide && <WelcomeBackground />}
      
      {/* Spotlight */}
      {!step.fullScreen && Object.keys(spotlightPosition).length > 0 && (
        <Spotlight style={spotlightPosition} isPositioning={isPositioning} />
      )}
      
      {/* Clickable area */}
      {!step.fullScreen && Object.keys(clickablePosition).length > 0 && (
        <ClickableArea 
          style={clickablePosition}
          onClick={handleSpotlightClick}
          isPositioning={isPositioning}
        />
      )}
      
      {/* Tooltip */}
      <Tooltip 
        style={tooltipPosition}
        className={`${tooltipPosition.className || ''} ${showBreathingAnimation ? 'breathing' : ''}`}
        isWelcomeSlide={isWelcomeSlide}
        isPositioning={isPositioning && !isWelcomeSlide}
      >
        {/* Skip button at top right - only for non-welcome slides */}
        {!isWelcomeSlide && (
        <div style={{ 
          display: 'flex', 
            justifyContent: 'space-between', 
          alignItems: 'flex-start', 
            marginBottom: '16px',
            width: '100%'
        }}>
            <TooltipTitle style={{ margin: 0, flex: 1 }}>{step.title}</TooltipTitle>
          <TutorialButton 
            onClick={handleClose} 
            style={{ 
                marginLeft: '12px', 
              padding: '4px 12px', 
                fontSize: '11px'
            }}
              isWelcomeSlide={false}
          >
            Skip
          </TutorialButton>
        </div>
        )}
        
        {isWelcomeSlide && (
          <TooltipTitle isWelcomeSlide={isWelcomeSlide}>{step.title}</TooltipTitle>
        )}
        
        <TooltipText isWelcomeSlide={isWelcomeSlide}>{step.description}</TooltipText>
        
        <ButtonRow style={{ justifyContent: isWelcomeSlide ? 'flex-start' : 'space-between' }}>
          <ButtonGroup style={{ 
            gap: isWelcomeSlide ? '30px' : '8px',
            flexWrap: 'wrap',
            justifyContent: isWelcomeSlide ? 'flex-start' : 'flex-start'
          }}>
            {isWelcomeSlide ? (
              <>
                <TutorialButton onClick={handleNext} isWelcomeSlide={isWelcomeSlide}>
                  Start
                </TutorialButton>
                <TutorialButton onClick={onClose} isWelcomeSlide={isWelcomeSlide}>
                  Skip
                </TutorialButton>
              </>
            ) : (
              <>
                <TutorialButton onClick={handleNext} isWelcomeSlide={isWelcomeSlide}>
                  {isLastStep ? 'Finish' : 'Next'}
                </TutorialButton>
            {!isFirstStep && (
              <TutorialButton onClick={handlePrevious} isWelcomeSlide={isWelcomeSlide}>
                Back
              </TutorialButton>
            )}
              </>
            )}
          </ButtonGroup>
          
          {!isWelcomeSlide && (
            <StepCounter>
              {currentStep + 1} of {TUTORIAL_STEPS.length}
            </StepCounter>
          )}
        </ButtonRow>
        

      </Tooltip>
    </TutorialOverlay>
  );
};

export default DizeeTutorial; 